import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { 
  TextField, 
  Grid, 
  Typography, 
  Box,
  Paper,
  Chip,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import { useRegistration } from '../../../context/RegistrationContext';

// Validation schema
const validationSchema = Yup.object({
  specializations: Yup.array()
    .min(1, 'Select at least one specialization')
    .required('Specializations are required'),
  experienceYears: Yup.number()
    .required('Experience is required')
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .min(1, 'Minimum 1 year of experience'),
  bio: Yup.string()
    .required('Bio is required')
    .min(100, 'Bio must be at least 100 characters')
    .max(1000, 'Bio cannot exceed 1000 characters'),
  consultationFee: Yup.number()
    .required('Consultation fee is required')
    .positive('Must be a positive number')
    .min(10, 'Minimum fee is $10')
});

// Available specializations
const availableSpecializations = [
  'Vedic Astrology',
  'Western Astrology',
  'Tarot Reading',
  'Numerology',
  'Palmistry',
  'Vastu Shastra',
  'Feng Shui',
  'Face Reading',
  'Crystal Healing',
  'Gemology',
  'Horary Astrology',
  'KP Astrology',
  'Nadi Astrology'
];

const ProfessionalInfoStep = () => {
  const { formData, updateFormData, setStepValidity } = useRegistration();
  
  const formik = useFormik({
    initialValues: {
      specializations: formData.professional.specializations || [],
      experienceYears: formData.professional.experienceYears || '',
      bio: formData.professional.bio || '',
      consultationFee: formData.professional.consultationFee || ''
    },
    validationSchema,
    onSubmit: (values) => {
      // This step uses automatic validations and data saving
    },
    validateOnMount: true
  });
  
  // Update context when values change
  useEffect(() => {
    updateFormData('professional', formik.values);
  }, [formik.values, updateFormData]);
  
  // Update form validity when there are changes
  useEffect(() => {
    setStepValidity('professional', formik.isValid);
  }, [formik.isValid, setStepValidity]);
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Professional Information
      </Typography>
      
      <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          These details will appear on your public profile and help clients find you based on your expertise.
          Be specific about your specializations and experience to attract the right clients.
        </Typography>
      </Paper>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            id="specializations"
            options={availableSpecializations}
            value={formik.values.specializations}
            onChange={(_, newValue) => {
              formik.setFieldValue('specializations', newValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip 
                  variant="outlined" 
                  label={option} 
                  {...getTagProps({ index })} 
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Specializations"
                error={formik.touched.specializations && Boolean(formik.errors.specializations)}
                helperText={formik.touched.specializations && formik.errors.specializations}
                placeholder="Select your specializations"
                required
              />
            )}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="experienceYears"
            name="experienceYears"
            label="Years of Experience"
            type="number"
            value={formik.values.experienceYears}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.experienceYears && Boolean(formik.errors.experienceYears)}
            helperText={formik.touched.experienceYears && formik.errors.experienceYears}
            InputProps={{ inputProps: { min: 1 } }}
            required
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="consultationFee"
            name="consultationFee"
            label="Consultation Fee (per hour)"
            type="number"
            value={formik.values.consultationFee}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.consultationFee && Boolean(formik.errors.consultationFee)}
            helperText={formik.touched.consultationFee && formik.errors.consultationFee}
            InputProps={{ 
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: { min: 10 } 
            }}
            required
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="bio"
            name="bio"
            label="Professional Bio"
            multiline
            rows={6}
            value={formik.values.bio}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.bio && Boolean(formik.errors.bio)}
            helperText={
              (formik.touched.bio && formik.errors.bio) || 
              `${formik.values.bio.length}/1000 characters. Describe your experience, approach, and what clients can expect.`
            }
            required
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfessionalInfoStep;
